import { useState, useMemo } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import type { EmpresaDetailed } from '@/types'
import {
  initialForm,
  type DealFormData,
  type FieldError,
} from '@/components/RocketSearch/rocketDealsFormTypes'

export function useRocketDealsForm(onClose: () => void, onSuccess: () => void) {
  const [mode, setMode] = useState<'new' | 'existing'>('new')
  const [selectedCompany, setSelectedCompany] = useState<EmpresaDetailed | null>(null)
  const [form, setForm] = useState<DealFormData>({ ...initialForm })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<FieldError[]>([])

  const dealNome = useMemo(() => {
    const base = mode === 'existing'
      ? (selectedCompany?.nome || selectedCompany?.razaoSocial || '')
      : form.empresaNome
    return base ? `${base} - Rocket Search` : ''
  }, [mode, selectedCompany, form.empresaNome])

  const getError = (field: string) => errors.find((e) => e.field === field)?.message

  const setField = <K extends keyof DealFormData>(key: K, value: DealFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => prev.filter((e) => e.field !== key))
  }

  const validate = (): boolean => {
    const errs: FieldError[] = []

    if (mode === 'new') {
      if (!form.empresaNome.trim()) errs.push({ field: 'empresaNome', message: 'Nome da empresa é obrigatório' })
      if (!form.contato.trim()) errs.push({ field: 'contato', message: 'Contato é obrigatório' })
      if (!form.telefone.trim()) errs.push({ field: 'telefone', message: 'Telefone é obrigatório' })
    }

    if (!form.dealNome.trim()) errs.push({ field: 'dealNome', message: 'Nome do deal é obrigatório' })
    if (form.valor <= 0) errs.push({ field: 'valor', message: 'Valor deve ser maior que zero' })

    setErrors(errs)
    return errs.length === 0
  }

  const handleClose = () => {
    setForm({ ...initialForm })
    setSelectedCompany(null)
    setMode('new')
    setErrors([])
    onClose()
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        nome: form.dealNome.trim(),
        valor: form.valor,
        probabilidade: form.probabilidade,
        observacoes: form.observacoes.trim(),
        servico: 'Rocket Search',
      }

      if (mode === 'existing' && selectedCompany) {
        payload.empresaId = selectedCompany.id
        payload.empresaNome = selectedCompany.nome || selectedCompany.razaoSocial
      } else {
        payload.empresaNome = form.empresaNome.trim()
        payload.cnpj = form.cnpj.trim()
        payload.site = form.site.trim()
        payload.contato = form.contato.trim()
        payload.telefone = form.telefone.trim()
        payload.email = form.email.trim()
        payload.segmento = form.segmento
        payload.plataforma = form.plataforma
        payload.modelo = form.modelo
        payload.faturamento = form.faturamento
      }

      await api.post('/rocket-deals', payload)
      toast.success('Deal criado com sucesso!')
      handleClose()
      onSuccess()
    } catch {
      toast.error('Erro ao criar deal. Tente novamente.')
    } finally {
      setSaving(false)
    }
  }

  const handleCompanySelect = (company: EmpresaDetailed) => {
    setSelectedCompany(company)
    setField('empresaNome', '')
    const dealName = `${company.nome || company.razaoSocial} - Rocket Search`
    setField('dealNome', dealName)
  }

  return {
    mode,
    setMode,
    selectedCompany,
    setSelectedCompany,
    form,
    saving,
    dealNome,
    getError,
    setField,
    handleClose,
    handleSubmit,
    handleCompanySelect,
  }
}
